import { deleteCookie, getCookie, setCookie } from '@toggle/helpers';
import { setDefaultOptions } from 'date-fns';
import { ja } from 'date-fns/locale';
import { changeLanguage } from 'i18next';

import { SupportedLanguages, useFlags } from '~/stores/use-flags/useFlags';
import { create } from '~/utils/store/createStore';

const NEXT_LOCALE_COOKIE_KEY = 'NEXT_LOCALE';
interface LanguageStore {
  languageSetting: string;
  initStore: (onLanguageChangeCallback?: () => void) => void;
  rollBackBrowserLanguage: () => void;
  selectLanguage: (languageSetting: string) => Promise<void>;
  onLanguageChangeCallback?: () => void;
}

export const useLanguageStore = create<LanguageStore>(set => ({
  languageSetting: '',
  initStore: onLanguageChangeCallback => {
    const { selectLanguage, rollBackBrowserLanguage } =
      useLanguageStore.getState();
    const languageSetting = getCookie(NEXT_LOCALE_COOKIE_KEY);

    set({
      onLanguageChangeCallback,
    });

    if (!!languageSetting && typeof languageSetting === 'string') {
      selectLanguage(languageSetting);
    } else if (!languageSetting) {
      rollBackBrowserLanguage();
    }
  },
  selectLanguage: async (languageSetting: string) => {
    const {
      rollBackBrowserLanguage,
      onLanguageChangeCallback,
      languageSetting: currentLanguage,
    } = useLanguageStore.getState();
    const dateFormattingLocale = languageSetting === 'ja' ? ja : undefined;

    set({
      languageSetting,
    });

    if (languageSetting === 'en') {
      deleteCookie(NEXT_LOCALE_COOKIE_KEY);
    } else {
      setCookie(NEXT_LOCALE_COOKIE_KEY, languageSetting, { expires: 365 });
    }

    try {
      await changeLanguage(languageSetting);
      setDefaultOptions({ locale: dateFormattingLocale });

      if (!!currentLanguage && currentLanguage !== languageSetting) {
        onLanguageChangeCallback?.();
      }
    } catch (e) {
      rollBackBrowserLanguage();
    }
  },
  rollBackBrowserLanguage: () => {
    const { systemFlags } = useFlags.getState();
    const { selectLanguage } = useLanguageStore.getState();
    const languages = systemFlags.languages;

    if (languages) {
      let browserLanguages = window.navigator.languages;

      // Workaround for Chromium
      // When user update their language, there is an issue on chromium, that doesnt update `navigator.languages` list order
      // But strangely `navigator.language` does update, so we manuall unshift it to `browserLanguages`
      if (browserLanguages[0] !== window.navigator.language) {
        browserLanguages = [window.navigator.language, ...browserLanguages];
      }

      const matchedLang = browserLanguages
        .find(
          element => languages[element.substring(0, 2) as SupportedLanguages]
        )
        ?.substring(0, 2);

      if (matchedLang) {
        selectLanguage(matchedLang);
      }
    }
  },
}));
